import { useMemo } from 'react';
import ChapterSection from '../ChapterSection/ChapterSection';
import ChapterMap from '../ChapterMap/ChapterMap';
import type { IconName } from '@/components/ui/Icon/Icon';
import type { Point, ReinfoLayerSummary, ReinfoPortfolio } from '@/lib/api.types';
import { LIFE_AMENITY_ENDPOINTS, LIFE_ICON_FOR } from '@/lib/chapters';
import type { GeometryLayer, LegendItem, MapSpec, MarkerSpec } from '@/lib/map-spec';
import type { LeadSpec, RowSpec } from '@/lib/report-content';
import { TILES } from '@/lib/tiles';
import { formatDistanceM } from '@/lib/format';
import { parseProspectsMetrics } from '@/lib/prospects';
import { ChapterLead, CheckList, EmptyState, RawDetails, RowList } from './shared';

type Props = {
  portfolio: ReinfoPortfolio;
  site: Point;
};

type AmenityDef = {
  endpoint: string;
  icon: IconName;
  color: string;
  label: string;
};

// 「その他の暮らし施設」マップに点で出す施設。学校(XKT006)と学区(XKT004/005)は
// 小学校・中学校で見やすさが落ちるため専用マップに分ける（下記）。
// endpoint は backend の LAYER_CONFIGS（不動産情報ライブラリの実コード）に一致させること。
const AMENITY_DEFS: AmenityDef[] = [
  { endpoint: 'XKT015', icon: 'train', color: '#7AA8C7', label: '駅' },
  { endpoint: 'XKT007', icon: 'building-community', color: '#C99A3B', label: '保育園・幼稚園' },
  { endpoint: 'XKT010', icon: 'stethoscope', color: '#C76D5A', label: '医療機関' },
  { endpoint: 'XKT011', icon: 'users-group', color: '#9BB069', label: '福祉施設' },
  { endpoint: 'XKT017', icon: 'notebook', color: '#8FA9C2', label: '図書館' },
  { endpoint: 'XKT018', icon: 'building-bank', color: '#B0A07A', label: '役場・集会施設' },
  { endpoint: 'XGT001', icon: 'shield-check', color: '#C28A3A', label: '避難場所' },
];

// 「学校」レイヤ(XKT006)は小・中・高をまとめて返すため、施設名から種別を判定する。
type SchoolKind = '小学校' | '中学校' | '高校' | '学校';

function schoolKindFromLabel(label: string | null | undefined): SchoolKind {
  const s = label ?? '';
  if (s.includes('小学')) return '小学校';
  if (s.includes('中学') || s.includes('中等')) return '中学校';
  if (s.includes('高校') || s.includes('高等')) return '高校';
  return '学校';
}

const SCHOOL_ENDPOINT = 'XKT006';
const SCHOOL_ICON: IconName = 'school';
const SCHOOL_KIND_COLOR: Record<SchoolKind, string> = {
  小学校: '#D9A441',
  中学校: '#C9863A',
  高校: '#B06A3A',
  学校: '#D9A441',
};

// 学区レイヤ(XKT004/005)は intersect の面で、表示半径内の隣接校区もまとめて返す。
// 全校区を同色で塗ると境界が潰れて見えないため、校区（学校名）ごとに別色を割り当てる。
// 淡い面塗りでも識別できる、落ち着いた中彩度の色を hue 順に散らして並べたパレット。
const DISTRICT_PALETTE = [
  '#6F97B8', // 藍
  '#9BB45E', // 若草
  '#C99A3B', // 黄土
  '#C0728C', // 紅梅
  '#5E9E8E', // 青緑
  '#B07AA8', // 藤紫
  '#C2795A', // 赤茶
  '#7E86C2', // 桔梗
  '#A8A24E', // 鶯
  '#C76D5A', // 朱
];
const DISTRICT_LEGEND_CAP = 8; // 凡例に校区名を並べる上限（超過分は「ほか N 校区」に集約）

const ELEMENTARY_DISTRICT = { endpoint: 'XKT004', label: '小学校区' };
const JUNIOR_DISTRICT = { endpoint: 'XKT005', label: '中学校区' };

const PER_CATEGORY_CAP = 5; // その他マップで 1 カテゴリが地図と凡例を独占しないよう均す
const SCHOOL_CAP = 8; // 学校マップ 1 種別あたりの上限
const SUBMAP_HEIGHT = 240;
const SUBMAP_ZOOM = 14;

type Submap = {
  key: string;
  caption: string;
  emptyMessage: string;
  markers: MarkerSpec[];
  geometries: GeometryLayer[];
  legend: LegendItem[];
};

type SchoolPoint = { point: Point; kind: SchoolKind; title: string };

function collectSchoolPoints(layer: ReinfoLayerSummary | undefined): SchoolPoint[] {
  if (!layer) return [];
  const geoms = layer.feature_geometries ?? [];
  const labels = layer.feature_labels ?? [];
  const out: SchoolPoint[] = [];
  for (let i = 0; i < geoms.length; i += 1) {
    const g = geoms[i];
    if (g.type !== 'Point') continue;
    const coords = g.coordinates as [number, number];
    const label = labels[i] ?? layer.value_label ?? null;
    out.push({
      point: { lon: coords[0], lat: coords[1] },
      kind: schoolKindFromLabel(label),
      title: label ?? '学校',
    });
  }
  return out;
}

type District = { label: string; color: string };

function districtGeometries(
  layer: ReinfoLayerSummary | undefined,
  fallbackLabel: string,
): { geometries: GeometryLayer[]; districts: District[] } {
  if (!layer) return { geometries: [], districts: [] };
  const geoms = layer.feature_geometries ?? [];
  // feature_labels は feature_geometries と同じ index で対応する（backend が同じループで構築）。
  // 各校区ポリゴンには「そのエリアの学校名」が入っている。layer.value_label は調査地点を含む
  // 代表エリアの学校名なので、全ポリゴンに使い回すと「どの校区も調査地点の学校」に見えてしまう。
  // → index を保ったまま feature_labels[i] を使う（filter().map() だと index がずれるので使わない）。
  const labels = layer.feature_labels ?? [];
  // 色は学校名で引く。同じ校区が複数ポリゴンに分かれても同色になる。名前が取れない
  // ポリゴンは index でキー分けし、別校区とみなして別色にする（同色で潰さない）。
  const colorByKey = new Map<string, string>();
  const districts: District[] = [];
  const geometries: GeometryLayer[] = [];
  for (let i = 0; i < geoms.length; i += 1) {
    const g = geoms[i];
    if (g.type !== 'Polygon' && g.type !== 'MultiPolygon') continue;
    const label = labels[i] ?? layer.value_label ?? fallbackLabel;
    const key = labels[i] ?? `__idx_${i}`;
    let color = colorByKey.get(key);
    if (!color) {
      color = DISTRICT_PALETTE[colorByKey.size % DISTRICT_PALETTE.length];
      colorByKey.set(key, color);
      districts.push({ label, color });
    }
    geometries.push({
      data: g,
      style: { color, weight: 2, fillColor: color, fillOpacity: 0.25 },
      label,
    });
  }
  return { geometries, districts };
}

// 学校種別ごとの専用マップ（小学校 / 中学校）。該当種別のピン＋対応する学区ポリゴン。
function buildSchoolSubmap(
  key: string,
  kind: SchoolKind,
  schoolPoints: SchoolPoint[],
  district: { endpoint: string; label: string },
  districtLayer: ReinfoLayerSummary | undefined,
  caption: string,
): Submap {
  const color = SCHOOL_KIND_COLOR[kind];
  const markers: MarkerSpec[] = schoolPoints
    .filter((p) => p.kind === kind)
    .slice(0, SCHOOL_CAP)
    .map((p) => ({ point: p.point, icon: SCHOOL_ICON, color, title: p.title }));
  const { geometries, districts } = districtGeometries(districtLayer, district.label);
  const legend: LegendItem[] = [];
  if (markers.length > 0) legend.push({ icon: SCHOOL_ICON, color, label: kind });
  // 校区ごとに色分けしているので、凡例にも「色 → 校区名」の対応を並べる。
  // 多すぎる場合は上限までに絞り、残数を「ほか N 校区」と明示する（黙って切り捨てない）。
  districts.slice(0, DISTRICT_LEGEND_CAP).forEach((d) => {
    legend.push({ color: d.color, label: d.label });
  });
  if (districts.length > DISTRICT_LEGEND_CAP) {
    // 「…」は字形マーカーとして emoji 経由で描画（color は字形/アイコン凡例では未使用）。
    legend.push({ emoji: '…', color: 'transparent', label: `ほか ${districts.length - DISTRICT_LEGEND_CAP} 校区` });
  }
  return {
    key,
    caption,
    emptyMessage: `この付近では${kind}・${district.label}は見つかりませんでした。`,
    markers,
    geometries,
    legend,
  };
}

// その他の暮らし施設マップ。駅・医療・保育などのポイント＋（小中以外の）高校・学校ピン。
function buildOtherSubmap(
  amenityLayers: ReinfoLayerSummary[],
  otherSchoolPoints: SchoolPoint[],
): Submap {
  const markers: MarkerSpec[] = [];
  const legendMap = new Map<string, LegendItem>();

  for (const p of otherSchoolPoints.slice(0, SCHOOL_CAP)) {
    const color = SCHOOL_KIND_COLOR[p.kind];
    markers.push({ point: p.point, icon: SCHOOL_ICON, color, title: p.title });
    legendMap.set(p.kind, { icon: SCHOOL_ICON, color, label: p.kind });
  }

  for (const def of AMENITY_DEFS) {
    const layer = amenityLayers.find((l) => l.endpoint === def.endpoint);
    if (!layer) continue;
    const geoms = layer.feature_geometries ?? [];
    const labels = layer.feature_labels ?? [];
    let added = 0;
    for (let i = 0; i < geoms.length && added < PER_CATEGORY_CAP; i += 1) {
      const g = geoms[i];
      if (g.type !== 'Point') continue;
      const coords = g.coordinates as [number, number];
      markers.push({
        point: { lon: coords[0], lat: coords[1] },
        icon: def.icon,
        color: def.color,
        title: labels[i] ?? layer.value_label ?? def.label,
      });
      legendMap.set(def.label, { icon: def.icon, color: def.color, label: def.label });
      added += 1;
    }
    // Point ジオメトリが無いが nearest_point だけある場合は最寄り 1 点だけ出す。
    if (added === 0 && layer.nearest_point) {
      const distance =
        layer.nearest_distance_m != null ? formatDistanceM(layer.nearest_distance_m) : null;
      const labelText = layer.value_label || def.label;
      markers.push({
        point: layer.nearest_point,
        icon: def.icon,
        color: def.color,
        title: distance ? `${labelText} (${distance})` : labelText,
      });
      legendMap.set(def.label, { icon: def.icon, color: def.color, label: def.label });
    }
  }

  return {
    key: 'other',
    caption: '周辺の暮らし関連スポット（駅・医療・保育・高校など）',
    emptyMessage: 'この付近では暮らし関連スポットが見つかりませんでした。',
    markers,
    geometries: [],
    legend: [...legendMap.values()],
  };
}

function toMapSpec(submap: Submap, site: Point): MapSpec {
  return {
    key: submap.key,
    point: site,
    tileUrl: TILES.pale,
    zoom: SUBMAP_ZOOM,
    height: SUBMAP_HEIGHT,
    geometries: submap.geometries,
    markers: submap.markers,
    legend: submap.legend,
    caption: submap.caption,
    emptyMessage: submap.emptyMessage,
  };
}

export type EnvironmentContent = {
  lead: LeadSpec;
  maps: MapSpec[];
  rows: RowSpec[];
  checkItems: string[];
  emptyText: string | null;
};

// 暮らし章の中身。画面 (ChapterEnvironment) と PDF 冊子が共有する純関数。
export function buildEnvironmentContent(portfolio: ReinfoPortfolio, site: Point): EnvironmentContent {
  const amenityLayers = portfolio.layers.filter((l) => LIFE_AMENITY_ENDPOINTS.has(l.endpoint));
  const prospectsCheckItems = new Set(parseProspectsMetrics(portfolio.metrics)?.checkItems ?? []);

  const schoolLayer = amenityLayers.find((l) => l.endpoint === SCHOOL_ENDPOINT);
  const elemDistrict = amenityLayers.find((l) => l.endpoint === ELEMENTARY_DISTRICT.endpoint);
  const juniorDistrict = amenityLayers.find((l) => l.endpoint === JUNIOR_DISTRICT.endpoint);
  const schoolPoints = collectSchoolPoints(schoolLayer);

  const elementary = buildSchoolSubmap(
    'elementary',
    '小学校',
    schoolPoints,
    ELEMENTARY_DISTRICT,
    elemDistrict,
    '周辺の小学校と通学区域（小学校区）',
  );
  const junior = buildSchoolSubmap(
    'junior',
    '中学校',
    schoolPoints,
    JUNIOR_DISTRICT,
    juniorDistrict,
    '周辺の中学校と通学区域（中学校区）',
  );
  const otherSchoolPoints = schoolPoints.filter(
    (p) => p.kind !== '小学校' && p.kind !== '中学校',
  );
  const other = buildOtherSubmap(amenityLayers, otherSchoolPoints);

  const withContent = [elementary, junior, other].filter(
    (m) => m.markers.length > 0 || m.geometries.length > 0,
  );
  // 中身のあるマップだけ表示。全て空なら、その他マップ 1 枚だけ枠を出して emptyMessage を見せる。
  const submaps = withContent.length > 0 ? withContent : [other];

  const rows: RowSpec[] = amenityLayers.map((layer) => ({
    icon: LIFE_ICON_FOR[layer.endpoint] ?? 'building-community',
    label: layer.name,
    detail:
      layer.value_label ??
      (layer.matched_count > 0 ? `周辺 ${layer.matched_count} 件` : '近くに見つかりませんでした'),
    status: layer.matched_count > 0 ? 'safe' : 'neutral',
    statusLabel:
      layer.nearest_distance_m != null
        ? formatDistanceM(layer.nearest_distance_m)
        : layer.matched_count > 0
          ? '該当あり'
          : '該当なし',
  }));

  return {
    lead: { label: portfolio.label, message: portfolio.buyer_message },
    maps: submaps.map((m) => toMapSpec(m, site)),
    rows,
    // 見通し章由来の動的項目は暮らし章では出さない — portfolio を暮らし章/見通し章で共有しているため、
    // metrics["prospects"]["check_items"] と一致する項目を除いて「いまの暮らし」の確認に絞る。
    checkItems: portfolio.check_items.filter((item) => !prospectsCheckItems.has(item)),
    emptyText:
      amenityLayers.length === 0 ? '学校・医療・駅などの周辺施設データを取得できませんでした。' : null,
  };
}

export default function ChapterEnvironment({ portfolio, site }: Props) {
  const content = useMemo(() => buildEnvironmentContent(portfolio, site), [portfolio, site]);

  return (
    <ChapterSection id="environment">
      <ChapterLead {...content.lead} />

      {content.maps.map((spec) => (
        <ChapterMap key={spec.key} spec={spec} />
      ))}

      {content.emptyText ? (
        <EmptyState>{content.emptyText}</EmptyState>
      ) : (
        <RowList rows={content.rows} />
      )}

      <CheckList items={content.checkItems} />
      <RawDetails portfolio={portfolio} />
    </ChapterSection>
  );
}
